import { TweenLite, Bounce } from 'gsap';
import { CONFIG, KEYCODES, ANIMATIONS } from './../config';

import Hammer from './../../tools/hammer';

class BfvSliderBar {
  constructor(options = {}) {
    this.$root = options.element;
    this.$scrollable = this.$root.querySelector('.js-scrollable');
    this.$slidesArray = [...this.$root.querySelectorAll('.js-slide')];
    this.$prevButton = this.$root.querySelector('.js-prev-button');
    this.$nextButton = this.$root.querySelector('.js-next-button');
    this.$currentSlideIndicator = this.$root.querySelector('.js-current-slide-indicator');
    this.$maxSlideIndicator = this.$root.querySelector('.js-max-slide-indicator');
    this.index = 0;
    this.animatedIndex = null;
    this.bounceAnimationValue = 150;
    this.scrollPoints = [];
    this.$scrollable.lastHammerAction = '';
    this.isPanning = false;
    this.scrollOffset = this.$root.dataset.scrollOffset ? this.$root.dataset.scrollOffset : 0;
    this.$appointments = this.$root.querySelector('.js-bfv-termin-startpage');
  }

  init() {
    this.bindEvents();
    this.computeScrollPoints();
    this.updateActiveSlideIndicator();
    this.toggleNextPrevButtonVisibility();
  }

  /**
   * controls the visibility of the next and previous buttons
   */
  toggleNextPrevButtonVisibility() {
    if (this.index < this.scrollPoints.length - 1) {
      this.$nextButton.classList.add('js-button-visible');
    } else {
      this.$nextButton.classList.remove('js-button-visible');
    }

    if (this.index > 0) {
      this.$prevButton.classList.add('js-button-visible');
    } else {
      this.$prevButton.classList.remove('js-button-visible');
    }
  }

  /**
   * Calculates and stores the points where the next entry can't fit completely in the scroll space.
   * This creates points to tween to that allows the left most entry of the page to align at the left of the scroll area
   */
  computeScrollPoints() {
    this.scrollPoints = [0];
    let rightBounds = this.$root.offsetWidth;

    for (let i = 0; i < this.$slidesArray.length; i += 1) {
      // Check if this slide is hanging off right side of viewable scroll area, add it's left point to scrollPoints if it is
      if (i !== 0 && this.$slidesArray[i].offsetLeft + this.$slidesArray[i].offsetWidth > rightBounds) {
        this.scrollPoints.push(this.$slidesArray[i].offsetLeft);
        // Move the right side of viewable scroll area to match new scrollpoint
        rightBounds = this.$root.offsetWidth + this.$slidesArray[i].offsetLeft;
      }
    }

    if (this.$slidesArray.length > 1) {
      // Change the last scrollpoint so the last page doesn't have any as much whitespace
      let lastSlideWidth = this.$slidesArray[this.$slidesArray.length - 1].offsetWidth;

      for (let i = this.$slidesArray.length - 2; i >= 0; i -= 1) {
        const offsetWidth = this.$slidesArray[i + 1].offsetLeft - this.$slidesArray[i].offsetLeft;

        // Check if adding this slide to the last slide makes the last slide too wide for the viewable area
        if ((lastSlideWidth + offsetWidth) > this.$root.offsetWidth) {
          // Remove the current last slide and make the next slide the last slide (since adding this one would make it too wide)
          // Move new last slide over to fill white space to the right of last slide.
          this.scrollPoints.pop();
          this.scrollPoints.push(this.$slidesArray[i + 1].offsetLeft - (this.$root.offsetWidth - lastSlideWidth));
          break;
        } else {
          lastSlideWidth += offsetWidth;
        }
      }
    }
  }

  /**
   * update the slide page indicator
   */
  updateActiveSlideIndicator() {
    this.$currentSlideIndicator.innerText = this.index + 1;
    this.$maxSlideIndicator.innerHTML = this.scrollPoints.length;
  }

  /**
   * bind event listeners
   */
  bindEvents() {
    // listen for window resize to re-compute the scrollPoints in case of width change
    window.addEventListener(CONFIG.events.RESIZE, () => {
      this.computeScrollPoints();
      this.updateActiveSlideIndicator();
      // avoid the reset of the appointments slider
      if (this.$appointments !== null) {
        this.index = 0;
      }
      this.animateToIndex(true);
    });

    // left / right keys for active element scrolling
    const handleKeyDown = (event) => {
      const key = event.keyCode || event.which;
      if (key === KEYCODES.LEFT) {
        this.prev();
      } else if (key === KEYCODES.RIGHT) {
        this.next();
      }
    };

    // if the scroll gains focus allow keyboard input to scroll
    this.$root.addEventListener('focus', () => {
      document.activeElement.addEventListener('keydown', handleKeyDown, true);
    }, true);

    this.$root.addEventListener('blur', () => {
      document.activeElement.removeEventListener('keydown', handleKeyDown, true);
    }, true);

    // Peak Animation
    this.$nextButton.addEventListener('mouseenter', () => {
      this.peakInAnimation('NEXT');
    });
    this.$prevButton.addEventListener('mouseenter', () => {
      this.peakInAnimation('PREV');
    });
    this.$nextButton.addEventListener('mouseleave', () => {
      this.peakOutAnimation();
    });
    this.$prevButton.addEventListener('mouseleave', () => {
      this.peakOutAnimation();
    });

    // Click Events - Left / Right Arrows
    this.$nextButton.addEventListener('click', (ev) => {
      ev.preventDefault();
      this.next();
    });
    this.$prevButton.addEventListener('click', (ev) => {
      ev.preventDefault();
      this.prev();
    });

    // Click Events - Favorite Entries
    this.$slidesArray.forEach(($slide) => {
      const $innerSlide = $slide.querySelector('.js-slide-entry');

      if ($innerSlide) {
        $innerSlide.addEventListener('click', (evt) => {
          // check if the scroll area was also clicked and isn't trying to pan instead
          if (this.$scrollable.lastHammerAction !== 'tap') {
            evt.preventDefault();
            return;
          }

          if ($innerSlide.dataset.clickUrl) {
            document.location.href = $innerSlide.dataset.clickUrl;
          }
        });
      }
    });

    // Swipe and Drag
    let lastPanXValue = 0;
    let startPanXValue = 0;
    let ignorePan = false;
    const hammertime = new Hammer(this.$scrollable);
    hammertime.get('swipe')
      .set({ direction: Hammer.DIRECTION_HORIZONTAL });
    hammertime.get('pan')
      .set({ direction: Hammer.DIRECTION_HORIZONTAL });

    // SWIPE
    hammertime.on('swipeleft', () => {
      ignorePan = true;
      this.next();
    });

    hammertime.on('swiperight', () => {
      ignorePan = true;
      this.prev();
    });

    // PAN
    hammertime.on('panstart', (ev) => {
      ignorePan = false;
      lastPanXValue = ev.center.x;
      startPanXValue = lastPanXValue;
      this.$scrollable.lastHammerAction = 'panstart';
      this.isPanning = true;
    });

    hammertime.on('panright panleft', (ev) => {
      const currentPanXValue = ev.center.x;
      this.handlePan(lastPanXValue, currentPanXValue);
      lastPanXValue = currentPanXValue;
      this.$scrollable.lastHammerAction = 'panright panleft';
    });

    hammertime.on('panend pancancel', (ev) => {
      if (!ignorePan) {
        this.handlePanEnd(ev.center.x, startPanXValue);
      }

      // Reset
      lastPanXValue = 0;
      this.$scrollable.lastHammerAction = 'panend pancancel';
      this.isPanning = false;
    });

    hammertime.on('tap', () => {
      this.$scrollable.lastHammerAction = 'tap';
    });
  }

  /**
   * tween the scroll area as the user is dragging it
   */
  handlePan(lastPanXValue, currentPanXValue) {
    const diff = lastPanXValue - currentPanXValue;

    TweenLite.set(this.$scrollable, {
      x: `-=${diff}`,
    });
  }

  /**
   * check at pan end if the user has dragged enough to change the page or if it should animate back to original position
   */
  handlePanEnd(panendXValue, startPanXValue) {
    const completeXDiff = startPanXValue - panendXValue;

    if (completeXDiff > 100) {
      this.next();
    } else if (completeXDiff <= 0 && completeXDiff < -100) {
      this.prev();
    } else {
      this.animateToIndex();
    }
  }

  /**
   * check for a previous scroll page, set new current page, animate to it if possible
   */
  prev() {
    if (this.index === 0) {
      this.bounceAnimation('PREV');
      return;
    } else if (this.animatedIndex !== null) {
      return;
    }

    this.index -= 1;
    this.animateToIndex();
  }

  /**
   * check for a next scroll page, set new current page, animate to it if possible
   */
  next() {
    if (this.index >= (this.scrollPoints.length - 1)) {
      this.bounceAnimation('NEXT');
      return;
    } else if (this.animatedIndex !== null) {
      return;
    }
    this.index += 1;
    this.animateToIndex();
  }

  /**
   * tween to the scrollpoint of the current scroll index
   */
  animateToIndex(forceAnimation = false) {
    if (this.animatedIndex !== null && !forceAnimation) {
      return;
    }
    this.animatedIndex = this.index;

    this.updateActiveSlideIndicator();
    this.toggleNextPrevButtonVisibility();

    // Slide Animation
    TweenLite.killTweensOf(this.$scrollable);
    TweenLite.to(this.$scrollable, ANIMATIONS.default.DOUBLE_DURATION, {
      x: ((this.scrollPoints[this.index] * -1) - this.scrollOffset), // this will tween the scroll area to the pre-calculated points in the scrollPoints (scrollOffset can be passed to adjust the scroll from the left side of the slide)
      ease: ANIMATIONS.default.CURVE,
      onComplete: () => {
        this.animatedIndex = null;
      },
      onOverwrite: () => {
        this.animatedIndex = null;
      },
    });
  }

  /**
   * tween bounce animation from either end of the scroll area
   */
  bounceAnimation(calledFrom) {
    const indexPercentValue = this.scrollPoints[this.index] * -1;
    const toValue = (calledFrom === 'PREV') ? `${indexPercentValue + this.bounceAnimationValue}` : `${indexPercentValue - this.bounceAnimationValue}`;

    TweenLite.to(this.$scrollable, ANIMATIONS.default.DOUBLE_DURATION, {
      x: toValue,
    });
    TweenLite.to(this.$scrollable, ANIMATIONS.default.DOUBLE_DURATION, {
      x: indexPercentValue,
      delay: ANIMATIONS.default.DOUBLE_DURATION,
      ease: Bounce.easeOut,
    });
  }

  /**
   * tween in scroll area slightly to 'peak' at the next / previous page
   * cancel peak if currently panning
   */
  peakInAnimation(mode) {
    if (this.animatedIndex !== null || this.isPanning) {
      return;
    }

    const animationXValue = mode === 'NEXT' ? -80 : 80;

    TweenLite.killTweensOf(this.$scrollable);
    TweenLite.to(this.$scrollable, ANIMATIONS.default.DOUBLE_DURATION, {
      x: ((this.scrollPoints[this.index] * -1) + animationXValue),
      ease: ANIMATIONS.default.CURVE,
    });
  }

  /**
   * tween back to original position after peak ends
   * cancel peak if currently panning
   */
  peakOutAnimation() {
    if (this.isPanning) {
      return;
    }
    this.animateToIndex(true);
  }
}

export default BfvSliderBar;



// WEBPACK FOOTER //
// ./src/javascript/main/BfvSliderBar/index.js